import { useState, type FC } from 'react';
import { SparklesIcon } from './CourseStudentIcons';

interface StudentAIModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAskAI: (prompt: string) => Promise<{ answer: string; recommended_topics: string[]; sources: string[] }>;
}

export const StudentAIModal: FC<StudentAIModalProps> = ({ isOpen, onClose, onAskAI }) => {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<
    Array<{ role: 'user' | 'assistant'; text: string; topics?: string[]; sources?: string[] }>
  >([
    {
      role: 'assistant',
      text: 'Здравствуйте! Я интеллектуальный академический помощник Super AI. Могу объяснить сложные темы, помочь с рубриками заданий СРО, разобрать комментарии преподавателя и дать рекомендации по библиографии.',
    },
  ]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!query.trim() || isLoading) return;
    const userText = query.trim();
    setQuery('');
    setMessages((prev) => [...prev, { role: 'user', text: userText }]);
    setIsLoading(true);
    try {
      const response = await onAskAI(userText);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: response.answer,
          topics: response.recommended_topics,
          sources: response.sources,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: 'Для выполнения СРО рекомендую использовать источники из раздела «Найти литературу» и опираться на дескрипторы рубрики оценивания.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-500/40 bg-cyan-950/60 text-cyan-400">
              <SparklesIcon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Super AI · Помощник обучающегося</h3>
              <p className="text-[11px] text-slate-400">
                Контекстный учебный AI: объясняет материал и комментарии без изменения баллов
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        <div className="max-h-80 overflow-y-auto space-y-3 pr-1 text-xs">
          {messages.map((m, idx) => (
            <div
              key={idx}
              className={`p-3.5 rounded-xl ${
                m.role === 'user'
                  ? 'bg-cyan-950/70 border border-cyan-500/40 text-cyan-200 ml-6'
                  : 'bg-slate-900/90 border border-slate-800 text-slate-300 mr-6'
              }`}
            >
              <p className="leading-relaxed">{m.text}</p>
              {m.topics && m.topics.length > 0 && (
                <div className="mt-2.5 pt-2 border-t border-slate-800/80 flex flex-wrap gap-1.5">
                  <span className="text-[10px] text-slate-400 font-bold">Темы:</span>
                  {m.topics.map((t) => (
                    <span
                      key={t}
                      className="px-2 py-0.5 rounded bg-slate-800 text-cyan-300 text-[10px]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              {m.sources && m.sources.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1 text-[10px] text-slate-400">
                  <span className="font-semibold">Источники: </span>
                  <span>{m.sources.join(', ')}</span>
                </div>
              )}
            </div>
          ))}
          {isLoading && (
            <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 text-xs italic">
              Super AI формулирует академический ответ...
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-2 border-t border-slate-800">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Задайте вопрос по теме, рубрике задания или замечанию преподавателя..."
            className="flex-1 rounded-xl border border-slate-700 bg-slate-900/90 px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={isLoading || !query.trim()}
            className="rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] px-4 py-2.5 text-xs font-bold text-slate-950 transition-all shadow-md disabled:opacity-50"
          >
            Спросить
          </button>
        </div>
      </div>
    </div>
  );
};
